// advance-search/hooks/useAdvanceSearch.ts
import { useCallback, useMemo, useState } from "react";
import { OPERATOR_MAP } from "../operatorMap";
import { FieldSchema, FieldType, OperatorValue, RowState } from "../types";

function findField(fields: FieldSchema[], name: string): FieldSchema | undefined {
  return fields.find((f) => f.name === name);
}

function getFieldType(fields: FieldSchema[], name: string): FieldType {
  return findField(fields, name)?.type ?? "text";
}

function firstOperatorFor(fields: FieldSchema[], fieldName: string): OperatorValue {
  const t = getFieldType(fields, fieldName);
  return OPERATOR_MAP[t][0];
}

function makeNewRow(field: FieldSchema): RowState {
  const op = OPERATOR_MAP[field.type][0];
  if (op === "between") {
    return {
      id: crypto.randomUUID(),
      fieldName: field.name,
      fieldType: field.type,
      operator: "between",
      value: "",
      value2: "",
      multiTableSearch: field.multiTableSearch,
      lookupFieldName: field.lookupFieldName
    };
  }
  if (field.type === "json") {
    return {
      id: crypto.randomUUID(),
      fieldName: field.name,
      fieldType: field.type,
      operator: op,
      value: "",
      multiTableSearch: field.multiTableSearch,
      jsonPath: field.jsonPath,
      lookupFieldName: field.lookupFieldName
    };
  }
  return {
    id: crypto.randomUUID(),
    fieldName: field.name,
    fieldType: field.type,
    operator: op,
    value: "",
    multiTableSearch: field.multiTableSearch,
    lookupFieldName: field.lookupFieldName
  };
}

export interface UseAdvanceSearchOptions {
  fields: FieldSchema[];
  limitRows?: number;
}

export function useAdvanceSearch({ fields, limitRows }: UseAdvanceSearchOptions) {
  const [rows, setRows] = useState<RowState[]>([makeNewRow(fields[0])]);

  const updateRows = useCallback((next: RowState[]) => {
    setRows(next);
  }, []);

  const operatorsForField = useCallback(
    (fieldName: string): OperatorValue[] => {
      const t = getFieldType(fields, fieldName);
      return OPERATOR_MAP[t];
    },
    [fields]
  );

  const addRow = useCallback(() => {
    if (limitRows && rows.length >= limitRows) return;

    const lastRow = rows[rows.length - 1];
    const lastField = fields.find((f) => f.name === lastRow?.fieldName) ?? fields[0];
    updateRows([...rows, makeNewRow(lastField)]);
  }, [rows, fields, updateRows, limitRows]);

  const removeRow = useCallback(
    (id: string) => {
      if (rows.length === 1) return;
      updateRows(rows.filter((r) => r.id !== id));
    },
    [rows, updateRows]
  );

  const clearRow = useCallback(
    (id: string) => {
      updateRows(
        rows.map((r) => {
          if (r.id !== id) return r;
          const nextOp = firstOperatorFor(fields, r.fieldName);
          return nextOp === "between" ?
              {
                id: r.id,
                fieldName: r.fieldName,
                fieldType: r.fieldType,
                operator: "between",
                value: "",
                value2: "",
                multiTableSearch: r.multiTableSearch,
                jsonPath: r.jsonPath,
                lookupFieldName: r.lookupFieldName
              }
            : {
                id: r.id,
                fieldName: r.fieldName,
                fieldType: r.fieldType,
                operator: nextOp,
                value: "",
                multiTableSearch: r.multiTableSearch,
                jsonPath: r.jsonPath,
                lookupFieldName: r.lookupFieldName
              };
        })
      );
    },
    [rows, fields, updateRows]
  );

  const clearAllRow = useCallback(() => {
    updateRows([makeNewRow(fields[0])]);
  }, [fields, updateRows]);

  const changeField = useCallback(
    (id: string, fieldName: string) => {
      updateRows(
        rows.map((r) => {
          if (r.id !== id) return r;
          const newRow = makeNewRow(fields.find((f) => f.name === fieldName) || fields[0]);
          return { ...newRow, id: r.id }; // new row data but same id
        })
      );
    },
    [rows, fields, updateRows]
  );

  const changeOperator = useCallback(
    (id: string, operator: OperatorValue) => {
      updateRows(
        rows.map((r) => {
          if (r.id !== id) return r;
          if (operator === "between") {
            return {
              id: r.id,
              fieldName: r.fieldName,
              fieldType: r.fieldType,
              multiTableSearch: r.multiTableSearch,
              lookupFieldName: r.lookupFieldName,
              jsonPath: r.jsonPath,
              operator,
              value: "",
              value2: ""
            };
          }
          return {
            id: r.id,
            fieldName: r.fieldName,
            fieldType: r.fieldType,
            multiTableSearch: r.multiTableSearch,
            lookupFieldName: r.lookupFieldName,
            jsonPath: r.jsonPath,
            operator,
            value: ""
          };
        })
      );
    },
    [rows, updateRows]
  );

  const changeValue = useCallback(
    (id: string, which: "value" | "value2", val: string) => {
      updateRows(
        rows.map((r) => {
          if (r.id !== id) return r;
          if (which === "value2" && r.operator !== "between") return r;
          return { ...r, [which]: val } as RowState;
        })
      );
    },
    [rows, updateRows]
  );

  const fieldOptions = useMemo(
    () =>
      fields.map((f) => ({
        value: f.name,
        label: f.label ?? f.name
      })),
    [fields]
  );

  return {
    rows,
    addRow,
    removeRow,
    clearRow,
    clearAllRow,
    changeField,
    changeOperator,
    changeValue,
    operatorsForField,
    fieldOptions
  };
}
